'use client'

import { useState } from 'react'
import { X, ThumbsUp, ThumbsDown, Star } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'

interface BuddyReviewModalProps {
  isOpen: boolean
  onClose: () => void
  buddyName: string
  buddyId: string
  matchingId: string
  onSubmit: (review: ReviewData) => Promise<void>
}

export interface ReviewData {
  matching_id: string
  reviewed_user_id: string
  punctuality_score: number
  safety_score: number
  manner_score: number
  communication_score: number
  comment?: string
}

interface ReviewItem {
  id: keyof Omit<ReviewData, 'matching_id' | 'reviewed_user_id' | 'comment'>
  title: string
  description: string
}

const reviewItems: ReviewItem[] = [
  {
    id: 'punctuality_score',
    title: '시간 약속',
    description: '약속 시간을 잘 지켰나요?'
  },
  {
    id: 'safety_score',
    title: '안전 수칙',
    description: '안전 규칙을 잘 준수했나요?'
  },
  {
    id: 'manner_score',
    title: '매너',
    description: '예의 바르고 배려심이 있었나요?'
  },
  {
    id: 'communication_score',
    title: '소통',
    description: '원활하게 소통했나요?'
  }
]

const scoreOptions = [
  { value: 100, label: '매우 좋음', emoji: '😊', color: 'text-green-500', temp: '+0.1°C' },
  { value: 75, label: '좋음', emoji: '🙂', color: 'text-blue-500', temp: '+0.08°C' },
  { value: 50, label: '보통', emoji: '😐', color: 'text-yellow-500', temp: '+0.05°C' },
  { value: 25, label: '아쉬움', emoji: '😞', color: 'text-orange-500', temp: '+0.03°C' },
  { value: 0, label: '매우 아쉬움', emoji: '😢', color: 'text-red-500', temp: '-0.1°C' }
]

export default function BuddyReviewModal({
  isOpen,
  onClose,
  buddyName,
  buddyId,
  matchingId,
  onSubmit
}: BuddyReviewModalProps) {
  const { theme } = useTheme()
  const [scores, setScores] = useState<Record<string, number>>({})
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleScoreChange = (itemId: string, score: number) => {
    setScores(prev => ({ ...prev, [itemId]: score }))
  }

  const isFormComplete = reviewItems.every(item => scores[item.id] !== undefined)

  const handleSubmit = async () => {
    if (!isFormComplete) return

    setIsSubmitting(true)
    try {
      const reviewData: ReviewData = {
        matching_id: matchingId,
        reviewed_user_id: buddyId,
        punctuality_score: scores.punctuality_score,
        safety_score: scores.safety_score,
        manner_score: scores.manner_score,
        communication_score: scores.communication_score,
        comment: comment.trim() || undefined
      }

      await onSubmit(reviewData)
      onClose()
    } catch (error) {
      console.error('Failed to submit review:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const averageScore = Object.values(scores).length > 0
    ? Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length
    : 0

  const getAverageColor = () => {
    if (averageScore >= 90) return 'text-green-500'
    if (averageScore >= 70) return 'text-blue-500'
    if (averageScore >= 50) return 'text-yellow-500'
    if (averageScore >= 25) return 'text-orange-500'
    return 'text-red-500'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl ${
        theme === 'light'
          ? 'bg-white'
          : 'bg-gray-800'
      }`}>
        {/* 헤더 */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
          theme === 'light'
            ? 'bg-white border-gray-200'
            : 'bg-gray-800 border-gray-700'
        }`}>
          <div>
            <h2 className={`text-xl sm:text-2xl font-semibold ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              버디 평가하기
            </h2>
            <p className={`text-sm mt-1 ${
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {buddyName}님과의 다이빙은 어떠셨나요?
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'light'
                ? 'hover:bg-gray-100 text-gray-500'
                : 'hover:bg-gray-700 text-gray-400'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 평가 항목 */}
        <div className="p-6 space-y-8">
          {reviewItems.map((item, index) => (
            <div key={item.id} className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className={`font-medium ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>
                    {index + 1}. {item.title}
                  </h3>
                  <p className={`text-sm ${
                    theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {item.description}
                  </p>
                </div>
                {scores[item.id] !== undefined && (
                  <div className={`text-sm font-medium ${
                    scoreOptions.find(opt => opt.value === scores[item.id])?.color
                  }`}>
                    {scoreOptions.find(opt => opt.value === scores[item.id])?.temp}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-5 gap-2">
                {scoreOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleScoreChange(item.id, option.value)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      scores[item.id] === option.value
                        ? theme === 'light'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-blue-400 bg-blue-400/10'
                        : theme === 'light'
                        ? 'border-gray-200 hover:border-gray-300'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="text-2xl mb-1">{option.emoji}</div>
                    <div className={`text-xs font-medium ${
                      scores[item.id] === option.value
                        ? option.color
                        : theme === 'light'
                        ? 'text-gray-600'
                        : 'text-gray-400'
                    }`}>
                      {option.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* 평균 점수 표시 */}
          {Object.values(scores).length > 0 && (
            <div className={`p-4 rounded-xl text-center ${
              theme === 'light'
                ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100'
                : 'bg-gradient-to-r from-gray-700 to-gray-800 border border-gray-600'
            }`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className={`text-sm font-medium ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-200'
                }`}>
                  종합 평가
                </span>
              </div>
              <div className={`text-3xl font-bold ${getAverageColor()}`}>
                {averageScore.toFixed(0)}점
              </div>
            </div>
          )}

          {/* 추가 코멘트 */}
          <div className="space-y-2">
            <label className={`block text-sm font-medium ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              추가 코멘트 (선택)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="함께한 다이빙에 대한 소감을 남겨주세요..."
              rows={3}
              maxLength={500}
              className={`w-full px-4 py-3 rounded-xl border resize-none ${
                theme === 'light'
                  ? 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                  : 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
            />
            <div className={`text-xs text-right ${
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {comment.length}/500
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className={`sticky bottom-0 p-6 border-t ${
          theme === 'light'
            ? 'bg-white border-gray-200'
            : 'bg-gray-800 border-gray-700'
        }`}>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className={`flex-1 px-6 py-3 rounded-xl font-medium transition-colors ${
                theme === 'light'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } disabled:opacity-50`}
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormComplete || isSubmitting}
              className={`flex-1 px-6 py-3 rounded-xl font-medium transition-colors ${
                isFormComplete && !isSubmitting
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-lg'
                  : theme === 'light'
                  ? 'bg-gray-200 text-gray-400'
                  : 'bg-gray-700 text-gray-500'
              } disabled:cursor-not-allowed`}
            >
              {isSubmitting ? '제출 중...' : '평가 완료'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
